import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
  AlertIcon,
  Tag,
  TagLabel,
  TagCloseButton,
} from '@chakra-ui/react';
import React, { useCallback, useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import { useNavigate } from 'react-router-dom';
import { faFile } from '@fortawesome/free-solid-svg-icons';
import { useMutation } from 'urql';
import { useTranslation } from 'react-i18next';
import { useSheets } from '../../hooks/sheets';
import awsExports from '../../aws-exports';
import {
  DescriptionReaderDocument,
  OrderDeepFragment,
  ProtanProjectStepEnum,
  ProtanProjectStepStatusEnum,
  UpdateProjectDocument,
} from '../../gql/graphql';
import { useProtanProjectSteps } from '../common/ProtanProjectStepsProvider';
import SparkelIcon from '../common/icon/SparkelIcon';

const TOAST_ID = 'sheet-upload-success';

type NewProjectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  order: OrderDeepFragment;
};

export default function ProtanUploadModal({
  isOpen,
  onClose,
  projectId,
  order,
}: NewProjectModalProps): React.ReactElement {
  const { t } = useTranslation('protan');
  const { createSheet } = useSheets(projectId);
  const toast = useToast();

  const { sheets } = useSheets(projectId);

  const [isUploading, setUploading] = useState(false);

  const [, descriptionReader] = useMutation(DescriptionReaderDocument);

  const { projectSteps, setStepStatus } = useProtanProjectSteps();

  const uploadSheets = useCallback(
    async (files: File[] | null) => {
      const uploadSheet = async (file: File) => {
        invariant(
          awsExports.attachments_bucket_name,
          'Bucket name is required to upload sheets'
        );

        try {
          // TODO: Allow user to set a name for the sheet
          const filename = file.name.replace(/\.pdf$/, '');
          // TODO: Add correct version handling
          await createSheet(file, filename, 1);
          if (!toast.isActive(TOAST_ID)) {
            toast({
              id: TOAST_ID,
              status: 'success',
              duration: 3000,
              title: t('upload.success'),
            });
          }
        } catch (err) {
          toast({
            status: 'error',
            duration: 3000,
            title: t('upload.failed'),
          });
        }
      };
      if (files) {
        await Promise.all([...files].map((file) => uploadSheet(file)));
      }
    },
    [createSheet, toast, t]
  );

  const inputRef = useRef<HTMLInputElement | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);

  const [projectName, setProjectName] = useState('');

  const readAndAnalyzeDescriptions = useCallback(async () => {
    setUploading(true);
    await uploadSheets(files);

    setStepStatus(
      ProtanProjectStepEnum.AiAnalysis,
      ProtanProjectStepStatusEnum.InProgress
    );

    await new Promise((resolve) => setTimeout(resolve, 100 * 60));
    await descriptionReader({
      input: {
        tableId: order.id,
        sheetIds: sheets?.map((sheet) => sheet.id),
        projectId,
      },
    });

    setUploading(false);
  }, [
    descriptionReader,
    files,
    order.id,
    projectId,
    setStepStatus,
    sheets,
    uploadSheets,
  ]);

  const navigate = useNavigate();

  const handleRemoveFile = (fileName: string) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
  };

  const handleUploadSheetNewVersion = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const filelist = e.target.files;
    if (filelist) {
      const fileArray = Array.from(filelist);
      setFiles((prevFiles) => [...prevFiles, ...fileArray]);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles) {
      const fileArray = Array.from(droppedFiles);
      const pdfFiles = fileArray.filter(
        (file) =>
          file.type === 'application/pdf' ||
          file.name.toLowerCase().endsWith('.pdf')
      );

      const rejected = fileArray
        .filter((file) => !pdfFiles.includes(file))
        .map((file) => file.name);

      if (rejected.length > 0) {
        setRejectedFiles(rejected);
      }

      setFiles((prevFiles) => [...prevFiles, ...pdfFiles]);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const [, updateProject] = useMutation(UpdateProjectDocument);

  const handleSubmit = useCallback(async () => {
    const result = await updateProject({
      input: {
        id: projectId,
        patch: { name: projectName },
      },
    });
    if (result.error) {
      toast({
        title: t('update-project-name.error'),
        status: 'error',
      });
    }
    readAndAnalyzeDescriptions();
    setStepStatus(
      ProtanProjectStepEnum.ParameterDefinition,
      ProtanProjectStepStatusEnum.InProgress
    );
    onClose();
  }, [
    updateProject,
    projectId,
    projectName,
    readAndAnalyzeDescriptions,
    setStepStatus,
    onClose,
    toast,
    t,
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('new-project')}</ModalHeader>
        <Divider orientation="horizontal" />

        <ModalBody>
          <VStack spacing={3}>
            <FormControl isRequired>
              <FormLabel>{t('common:project-name')}</FormLabel>
              <Input
                autoFocus
                placeholder={t('project-name.placeholder')}
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{t('upload-documents')}</FormLabel>
              <Box
                as={Button}
                colorScheme="gray"
                onClick={() => inputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                width={'100%'}
                height={files.length > 0 ? 'auto' : 100}
                paddingY={files.length > 0 ? 3 : 20}
              >
                <VStack gap={files.length > 0 ? 2 : 4}>
                  <SparkelIcon
                    icon={faFile}
                    size={files.length > 0 ? '1x' : '2x'}
                  />
                  <Text>
                    {files.length > 0
                      ? t('upload.more-files')
                      : t('upload.drag-drop')}
                  </Text>
                </VStack>
              </Box>
            </FormControl>
            <input
              type="file"
              multiple={true}
              accept="application/pdf"
              ref={inputRef}
              style={{ display: 'none' }}
              onChange={handleUploadSheetNewVersion}
            />

            {rejectedFiles.length > 0 && (
              <Alert status="warning" borderRadius="lg">
                <AlertIcon />
                <VStack align="start" width="100%">
                  <Text fontWeight="bold">{t('upload.rejected-files')}</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {rejectedFiles.map((fileName) => (
                      <Tag
                        key={fileName}
                        colorScheme="orange"
                        borderRadius="full"
                        variant="outline"
                        size="md"
                      >
                        <TagLabel>{fileName}</TagLabel>
                      </Tag>
                    ))}
                  </HStack>
                </VStack>
              </Alert>
            )}

            {files && files.length > 0 && (
              <Alert borderRadius={'lg'}>
                <AlertIcon />
                <VStack align={'start'} width="100%">
                  <Text fontWeight="bold">{t('upload.accepted-files')}</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {Array.from(files).map((file) => (
                      <Tag
                        key={file.name}
                        colorScheme="blue"
                        borderRadius="full"
                        variant="solid"
                        size="md"
                      >
                        <TagLabel>{file.name}</TagLabel>
                        <TagCloseButton
                          onClick={() => handleRemoveFile(file.name)}
                        />
                      </Tag>
                    ))}
                  </HStack>
                </VStack>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <Divider orientation="horizontal" mt={1} />
        <ModalFooter gap={2}>
          <Button
            isLoading={isUploading}
            isDisabled={projectName === '' || files.length === 0}
            colorScheme="brand"
            type="submit"
            onClick={handleSubmit}
          >
            {t('create')}
          </Button>
          <Button colorScheme="gray" onClick={onClose}>
            {t('common:cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
